import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { useLocation, useTitle } from 'react-use';

import { SessionsInfiniteTable } from '@/components/organisms/SessionsInfiniteTable/SessionsInfiniteTable';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import { useAccount } from '@/hooks/useAccount';
import useBrains from '@/hooks/useBrains';
import DisabledAnalytics from '@/modules/analytics/components/DisabledAnalytics';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

/**
 * Brain Logs page with infinite scrolling table
 * This is a new implementation that uses the SessionsInfiniteTable component
 * instead of the legacy SessionsFieldset component.
 */
export const BrainLogsInfinite = () => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  useTitle(t('pages.logs', { 0: brain?.name ?? '-' }));
  const { account } = useAccount();

  const isAnalyticsDisabled = account?.opt_out;

  const canRead = useSelector((state: RootState) => getPermissions(state, 'logs', actions.READ));

  // This is a workaround to hide the version tile on AI Agents page
  // We will remove this when we will totally replace Brains with AI Agents
  const location = useLocation();
  const isAIAgentsPath = location.pathname.includes('/ai-agents');

  // Get session_id from URL params if present
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useSegments(PageName.BRAIN_LOGS);

  if (isAnalyticsDisabled) {
    return <DisabledAnalytics />;
  }

  return (
    <PermissionsWrapper module="logs">
      <PageContentWrapper
        readOnly={!canRead}
        noGutters={isAIAgentsPath}
        // AIAgentLayout already pads around this page; don't clip toolbar focus rings
        overflowVisible={isAIAgentsPath}
      >
        <SessionsInfiniteTable sessionId={sessionId} />
      </PageContentWrapper>
    </PermissionsWrapper>
  );
};
